import { useCallback } from 'react';
import {
  CreateImportResult,
  ResourceCodeMappingUpsertItem,
} from '@shared/models/element-import.model';
import {
  ElementImportsData,
  MappingSaveScope,
  useElementImportsStore,
  useProjectImportsState,
} from './element-imports.store';

export type ElementImportsPending = { status: 'pending' };
export type ElementImportsError = {
  status: 'error';
  error: Error;
  refetch: () => Promise<void>;
};
export type ElementImportsReady = {
  status: 'ready';
  data: ElementImportsData;
  isFetching: boolean;
  refetch: () => Promise<void>;
  uploadCsv: (file: File) => Promise<CreateImportResult>;
  deleteImport: (importId: string) => Promise<void>;
  /** Persist a batch of code mappings in one transaction (all or nothing). */
  upsertMappings: (
    mappings: ResourceCodeMappingUpsertItem[],
    scope: MappingSaveScope,
  ) => Promise<void>;
};

export type UseElementImportsReturn =
  ElementImportsPending | ElementImportsError | ElementImportsReady;

/**
 * CSV element imports for one project bucket.
 *
 * State lives in the shared zustand store keyed by `projectId`, so multiple
 * consumers for the same project stay in sync. Fetches automatically on first
 * subscribe (same pattern as {@link useAiChat}).
 */
export const useElementImports = (
  projectId: number,
): UseElementImportsReturn => {
  const state = useProjectImportsState(projectId);

  const refetch = useCallback(
    () => useElementImportsStore.getState().fetchFor(projectId),
    [projectId],
  );

  const uploadCsv = useCallback(
    (file: File) =>
      useElementImportsStore.getState().uploadCsv(projectId, file),
    [projectId],
  );

  const deleteImport = useCallback(
    (importId: string) =>
      useElementImportsStore.getState().deleteImport(projectId, importId),
    [projectId],
  );

  const upsertMappings = useCallback(
    (mappings: ResourceCodeMappingUpsertItem[], scope: MappingSaveScope) =>
      useElementImportsStore
        .getState()
        .upsertMappings(projectId, mappings, scope),
    [projectId],
  );

  switch (state.status) {
    case 'idle':
    case 'fetching':
      return { status: 'pending' } as const;
    case 'error':
      return { status: 'error', error: state.error, refetch } as const;
    case 'ready':
      return {
        status: 'ready',
        data: state.data,
        isFetching: state.isFetching,
        refetch,
        uploadCsv,
        deleteImport,
        upsertMappings,
      } as const;
  }
};
